import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { experienceMatchesCategory } from '../utils/experienceCategories';
import { CATEGORIES, CATEGORY_DESCRIPTIONS, CONTENT_TYPES } from '../data/xr-experiences';
import { useAuth } from '../auth/AuthContext';
import { useSaved } from '../context/SavedContext';
import AuthPanel from './AuthPanel';
import AuthModal from './AuthModal';
import BentoGrid from './BentoGrid';
import BetaLabel from './BetaLabel';
import PersonalizeFeedCTA from './PersonalizeFeedCTA';
import { loadHomeFilters, saveHomeFilters, DEFAULT_HOME_FILTERS } from '../utils/appNavigationState';
import { matchesLocationQuery } from '../utils/experienceLocation';
import { experienceSupportsDevice } from '../utils/experienceDeviceAccess';
import {
  searchExperiences,
  buildSearchIndex,
  hasSearchQuery,
  SEARCH_PLACEHOLDER,
  SEARCH_EMPTY_MESSAGE
} from '../services/search/experienceSearch';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  position: relative;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 20px 0;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  font-weight: 800;
  margin: 0;
  background: linear-gradient(135deg, #ff00aa, #00f5ff, #ffee00);
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(255, 0, 170, 0.4);
  animation: shimmer 3s ease infinite;

  @keyframes shimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const TitleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 auto 0.5rem;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
`;

const UploadButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);
  }
`;

const AboutButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilterLabel = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  align-self: center;
  margin-right: 4px;
`;

const SearchWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: min(520px, 100%);
  padding: 12px 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 0.95rem;
  outline: none;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    border-color: rgba(0, 245, 255, 0.6);
    box-shadow: 0 0 0 3px rgba(0, 245, 255, 0.15);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const CategoryFilter = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const CategoryInstruction = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  text-align: center;
  margin: 0 0 2rem 0;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const Chip = styled.button`
  background: ${props => props.$active ? (props.color || '#888') : 'rgba(255, 255, 255, 0.05)'};
  border: 2px solid ${props => props.$active ? (props.color || '#888') : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 8px 14px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.color || 'rgba(255,255,255,0.4)'};
    transform: translateY(-1px);
  }
`;

const CategoryChip = styled(motion.button)`
  background: ${props => props.active ? props.color : 'rgba(255, 255, 255, 0.05)'};
  border: 2px solid ${props => props.active ? props.color : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 10px 18px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active
    ? `0 0 15px ${props.color}60, 0 0 30px ${props.color}30`
    : 'none'};

  &:hover {
    background: ${props => props.color};
    border-color: ${props => props.color};
    transform: translateY(-2px);
    box-shadow: 0 0 20px ${props => props.color}70, 0 5px 20px rgba(0, 0, 0, 0.3);
  }
`;

const LocationFilter = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto 1.5rem;
  padding-bottom: 2rem;
  width: min(520px, 100%);
`;

const EmptyState = styled.p`
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  font-size: 1rem;
  margin-top: 40px;
`;

const InlineEmptyState = styled.p`
  color: rgba(255, 255, 255, 0.75);
  text-align: center;
  font-size: 0.95rem;
  margin: 0 0 1rem;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
`;

function HomeScreen({
  experiences,
  loading,
  error,
  onExperienceClick,
  onNavigateToSaved,
  onNavigateToRecommendations,
  onNavigateToCreate,
  onNavigateToMyUploads,
  onNavigateToReview,
  onNavigateToAbout,
  onGoHome,
  homeResetKey = 0,
  canCreatePost,
  canReview
}) {
  const { user } = useAuth();
  const { requestSave, isSaved } = useSaved();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [pendingCreateAfterAuth, setPendingCreateAfterAuth] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => loadHomeFilters().selectedCategory);
  const [contentType, setContentType] = useState(() => loadHomeFilters().contentType);
  const [searchQuery, setSearchQuery] = useState(() => loadHomeFilters().searchQuery);
  const [locationQuery, setLocationQuery] = useState(() => loadHomeFilters().locationQuery);
  const [device, setDevice] = useState(() => loadHomeFilters().device || 'all');

  useEffect(() => {
    if (!homeResetKey) return;
    setSelectedCategory(DEFAULT_HOME_FILTERS.selectedCategory);
    setContentType(DEFAULT_HOME_FILTERS.contentType);
    setSearchQuery(DEFAULT_HOME_FILTERS.searchQuery);
    setLocationQuery(DEFAULT_HOME_FILTERS.locationQuery);
    setDevice(DEFAULT_HOME_FILTERS.device);
    saveHomeFilters(DEFAULT_HOME_FILTERS);
  }, [homeResetKey]);

  useEffect(() => {
    saveHomeFilters({ selectedCategory, contentType, searchQuery, locationQuery, device });
  }, [selectedCategory, contentType, searchQuery, locationQuery, device]);

  useEffect(() => {
    if (selectedCategory !== 'location-based' && locationQuery) {
      setLocationQuery('');
    }
  }, [selectedCategory, locationQuery]);

  useEffect(() => {
    if (user && pendingCreateAfterAuth) {
      setPendingCreateAfterAuth(false);
      setAuthModalOpen(false);
      onNavigateToCreate?.();
    }
  }, [user, pendingCreateAfterAuth, onNavigateToCreate]);

  const handleSubmitClick = () => {
    onNavigateToCreate?.();
  };

  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
    setPendingCreateAfterAuth(false);
  };

  const normalizedLocation = locationQuery.trim();
  const searchIndex = useMemo(() => buildSearchIndex(experiences), [experiences]);

  const filteredExperiences = useMemo(() => {
    let filtered = experiences;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((exp) => experienceMatchesCategory(exp, selectedCategory));
    }
    if (selectedCategory === 'location-based' && normalizedLocation) {
      filtered = filtered.filter((exp) => matchesLocationQuery(exp, normalizedLocation));
    }
    if (contentType !== 'all') {
      filtered = filtered.filter((exp) => exp.contentType === contentType);
    }
    if (device !== 'all') {
      filtered = filtered.filter((exp) => experienceSupportsDevice(exp, device));
    }
    if (hasSearchQuery(searchQuery)) {
      filtered = searchExperiences(filtered, searchQuery, { searchIndex });
    }
    return filtered;
  }, [
    experiences,
    selectedCategory,
    normalizedLocation,
    contentType,
    device,
    searchQuery,
    searchIndex
  ]);

  const emptyMessage = selectedCategory === 'location-based' && normalizedLocation
    ? 'No experiences found in this location yet.'
    : hasSearchQuery(searchQuery)
      ? SEARCH_EMPTY_MESSAGE
      : 'No experiences match that search.';
  const showEmptyResults = !loading && !error && filteredExperiences.length === 0;

  return (
    <Container>
      <Header>
        <HeaderTop>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <PersonalizeFeedCTA />
            {onNavigateToCreate && (
              <UploadButton
                type="button"
                onClick={handleSubmitClick}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Share An Experience
              </UploadButton>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {onNavigateToAbout && (
              <AboutButton type="button" onClick={onNavigateToAbout}>
                About Us
              </AboutButton>
            )}
          <AuthPanel
            onNavigateToSaved={onNavigateToSaved}
            onNavigateToRecommendations={onNavigateToRecommendations}
            onNavigateToCreate={onNavigateToCreate}
            onNavigateToMyUploads={onNavigateToMyUploads}
            onNavigateToReview={onNavigateToReview}
            canCreatePost={canCreatePost}
            canReview={canReview}
          />
          </div>
        </HeaderTop>
        <TitleButton
          type="button"
          onClick={onGoHome}
          aria-label="Return to home and reset filters"
        >
          <Title>RealityCheck</Title>
          <BetaLabel />
        </TitleButton>
        <Subtitle>Discover immersive experiences you&apos;ll love.</Subtitle>

        <SearchWrapper>
          <SearchInput
            type="search"
            placeholder={SEARCH_PLACEHOLDER}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </SearchWrapper>

        <CategoryFilter>
          <CategoryChip
            color="#888"
            active={selectedCategory === 'all'}
            onClick={() => setSelectedCategory('all')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            All
          </CategoryChip>
          {CATEGORIES.map(category => (
            <CategoryChip
              key={category.id}
              color={category.color}
              active={selectedCategory === category.id}
              onClick={() => setSelectedCategory(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.label}
            </CategoryChip>
          ))}
        </CategoryFilter>

        <FilterRow>
          <FilterLabel>Genre:</FilterLabel>
          <Chip $active={contentType === 'all'} onClick={() => setContentType('all')}>All</Chip>
          {CONTENT_TYPES.map(t => (
            <Chip key={t.id} $active={contentType === t.id} onClick={() => setContentType(t.id)}>{t.label}</Chip>
          ))}
        </FilterRow>

        <FilterRow>
          <FilterLabel>Device:</FilterLabel>
          <Chip $active={device === 'all'} onClick={() => setDevice('all')}>All</Chip>
          <Chip $active={device === 'mobile'} onClick={() => setDevice('mobile')}>Mobile</Chip>
          <Chip $active={device === 'headset'} onClick={() => setDevice('headset')}>Headset</Chip>
          <Chip $active={device === 'desktop'} onClick={() => setDevice('desktop')}>Desktop</Chip>
        </FilterRow>

        {CATEGORY_DESCRIPTIONS[selectedCategory] && (
          <CategoryInstruction>
            {CATEGORY_DESCRIPTIONS[selectedCategory]}
          </CategoryInstruction>
        )}

        {selectedCategory === 'location-based' && (
          <LocationFilter>
            <SearchInput
              type="search"
              id="location-search"
              placeholder="Search city, state, or country…"
              value={locationQuery}
              onChange={(event) => setLocationQuery(event.target.value)}
              aria-label="Search by city, state, or country"
            />
          </LocationFilter>
        )}

        {showEmptyResults && (
          <InlineEmptyState>{emptyMessage}</InlineEmptyState>
        )}
      </Header>

      {loading && <EmptyState>Loading experiences…</EmptyState>}
      {error && <EmptyState>{error}</EmptyState>}
      {!loading && !error && filteredExperiences.length > 0 && (
        <BentoGrid
          experiences={filteredExperiences}
          onExperienceClick={onExperienceClick}
          showSaveButton
          isSaved={isSaved}
          onSaveClick={requestSave}
        />
      )}

      <AuthModal
        open={authModalOpen}
        mode={authMode}
        onClose={handleAuthModalClose}
        onModeChange={setAuthMode}
      />
    </Container>
  );
}

export default HomeScreen;
